import { autoUpdate, getOverflowAncestors } from "@floating-ui/dom";
import { ImageName } from "@mcleod/images";
import { Component, MainPageAccessor, Overlay, Panel } from "..";
import { Image } from "../components/image/Image";
import { DOMUtil } from "@mcleod/core";

/**
 * Creates a semi-transparent overlay that covers a target element.
 * The overlay automatically adjusts its size and position to only cover the visible portion
 * of the element, accounting for any overflow containers that might clip it.
 *
 * The simplest implementation would be to append this overlay to the target element itself.
 * However, there's no guarantee the component doesn't clear its this._element.innerHTML while the overlay is visible.
 */
export class ComponentOverlay {
    private overlay: Overlay;
    private targetElement: HTMLElement;
    private spinnerElement: HTMLElement;
    private cleanup: (() => void) | null = null;
    private isTopLevelLayout: boolean;

    constructor(target: Component | HTMLElement, showBusySpinner: boolean = true) {
        this.targetElement = target instanceof Component ? target._element : target;
        this.isTopLevelLayout = target instanceof Component && target.parent === MainPageAccessor.router;

        if (showBusySpinner) {
            this.spinnerElement = new Image({
                id: "spinner",
                name: ImageName.spinner,
                rotate: true,
                color: "primary",
                width: 48,
                height: 48
            })._element;
        }
    }

    public show(): ComponentOverlay {
        this.addAndPositionOverlay();
        return this;
    }

    private createOverlayDiv() {
        this.overlay = Overlay.showInOverlay(new Panel(), { greyedBackground: true, closeOnClickOff: false, coverPageHeader: false });
        if (this.spinnerElement) {
            this.overlay.overlayDiv.appendChild(this.spinnerElement);
        }

        Object.assign(this.overlay.overlayDiv.style, {
            position: "absolute",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
        });
    }

    public async addAndPositionOverlay() {

        if (!document.body.contains(this.targetElement)) {
            return;
        }

        this.createOverlayDiv();

         // Overlays on top level layouts don't need to be repositioned and this overlay will cover the router.
         // For all other components, use Floating UI to position the overlay over the target element
        if (!this.isTopLevelLayout) {
            const overlayDiv = this.overlay.overlayDiv;
            const targetElement = this.targetElement;

            const updatePosition = () => {
                if (this.overlay == null || !DOMUtil.areAllMounted(targetElement, overlayDiv)) {
                    this.hide();
                    return;
                }

                const rect = this.targetElement.getBoundingClientRect();
                this.updateOverlayPosition(rect);
                this.resizeSpinner(rect);
            };

            updatePosition();

            this.cleanup = autoUpdate(targetElement, overlayDiv, updatePosition);
        }
    }

    private updateOverlayPosition(rect: DOMRect) {
        // Get all elements that could clip this target element (scrollable containers, etc.)
        const overflowAncestors = getOverflowAncestors(this.targetElement);

        // Start with the full bounds and we'll programatically clip it down to the visible area
        const intersectionRect = {
            left: rect.left,
            top: rect.top,
            right: rect.right,
            bottom: rect.bottom
        };

        // Iterate through each ancestor to find the smallest shared visible area.
        for (const ancestor of overflowAncestors) {
            if (!(ancestor instanceof Element)) continue;

            const ancestorRect = ancestor.getBoundingClientRect();
            const ancestorStyle = window.getComputedStyle(ancestor);

            // Only apply clipping if this ancestor actually clips content
            if (ancestorStyle.overflow !== 'visible') {
                // Shrink our rect to fit within this ancestor's bounds
                intersectionRect.left = Math.max(intersectionRect.left, ancestorRect.left);
                intersectionRect.top = Math.max(intersectionRect.top, ancestorRect.top);
                intersectionRect.right = Math.min(intersectionRect.right, ancestorRect.right);
                intersectionRect.bottom = Math.min(intersectionRect.bottom, ancestorRect.bottom);
            }
        }

        // Calculate final dimensions and position the overlay
        const finalWidth = Math.max(0, intersectionRect.right - intersectionRect.left);
        const finalHeight = Math.max(0, intersectionRect.bottom - intersectionRect.top);

        // Apply the calculated position of the visible area to the overlay
        Object.assign(this.overlay.overlayDiv.style, {
            left: `${intersectionRect.left}px`,
            top: `${intersectionRect.top}px`,
            width: `${finalWidth}px`,
            height: `${finalHeight}px`
        });
    }

    private resizeSpinner(rect: DOMRect) {
        if (!this.spinnerElement) {
            return;
        }
        const smallerDim = Math.min(rect.width, rect.height);
        const spinnerSize = smallerDim < 48  ? smallerDim : 48;

        Object.assign(this.spinnerElement.style, {
            width: `${spinnerSize}px`,
            height: `${spinnerSize}px`
        });
    }

    public hide() {
        if (this.cleanup) {
            this.cleanup();
            this.cleanup = null;
        }
        if (DOMUtil.isMounted(this.overlay?.overlayDiv)) {
            this.overlay.remove();
        }
        this.overlay = null;
    }
}
